import React, { createContext, useContext } from 'react';
import type { WebChatWidgetProps } from './types';
import type { Message } from '../types/chat.types';
import type { Translations, Locale } from './i18n';
import { getTranslations, detectLocale } from './i18n';

interface ThemeContextValue {
  theme?: WebChatWidgetProps['theme'];
  headerText?: string;
  headerSubtitle?: string;
  avatarUrl?: string;
  enableNotifications?: boolean;
  telegramBotUrl?: string;
  assetBaseUrl?: string;
  locale: Locale;
  t: Translations;
  onMessageSent?: (message: string, images?: string[]) => void;
  onError?: (error: Error) => void;
  onMessageReceived?: (message: Message) => void;
  onMinimize?: () => void;
  onNewChat?: () => void;
  onDragStart?: (e: React.MouseEvent) => void;
  privacyPolicyUrl?: string;
  termsOfServiceUrl?: string;
  greeting?: string;
}

const defaultLocale = detectLocale();

const ThemeContext = createContext<ThemeContextValue>({
  locale: defaultLocale,
  t: getTranslations(defaultLocale),
});

// eslint-disable-next-line react-refresh/only-export-components
export const useTheme = () => useContext(ThemeContext);

// eslint-disable-next-line react-refresh/only-export-components
export const usePreset = () => {
  const { theme } = useContext(ThemeContext);
  return theme?.preset || 'classic';
};

// eslint-disable-next-line react-refresh/only-export-components
export const useAssetUrl = () => {
  const { assetBaseUrl } = useContext(ThemeContext);
  return (path: string) => {
    if (!assetBaseUrl) return path;
    return `${assetBaseUrl}${path}`;
  };
};

export const ThemeProvider: React.FC<{
  children: React.ReactNode;
  value: ThemeContextValue;
}> = ({ children, value }) => {
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};
